"use client";

// The departments as tabs. Every panel's words are in the page, stacked in one grid cell, so search
// engines read all of them and switching never changes the block's height. Only the open panel's
// picture is asked for until the block is about a screen away; the rest follow then, so nothing
// here competes with the first screen. On a wide screen it moves on by itself while in view, with a
// bar showing when, until the visitor picks a tab; hovering or focusing it holds it still.
import Image from "next/image";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ConciergeBell, ShieldCheck, Sun, UtensilsCrossed, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type ShowcaseItem = {
  icon: "front-desk" | "owners" | "control" | "restaurant";
  label: string;
  title: string;
  body: string;
  points: string[];
  image: { src: string; alt: string; width: number; height: number };
  /** The screen carries on below the picture's edge, so let it fade out rather than stop mid-line. */
  fade?: boolean;
};

const ICONS: Record<ShowcaseItem["icon"], LucideIcon> = { "front-desk": ConciergeBell, owners: Sun, control: ShieldCheck, restaurant: UtensilsCrossed };
const DWELL_MS = 7000;

export function Showcase({ items }: { items: ShowcaseItem[] }) {
  const [active, setActive] = useState(0);
  const [near, setNear] = useState(false);
  const [inView, setInView] = useState(false);
  const [auto, setAuto] = useState(false);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const bars = useRef<(HTMLSpanElement | null)[]>([]);
  const timer = useRef<Animation | null>(null);
  const touched = useRef(false);
  const id = useId();

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const ahead = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      setNear(true);
      ahead.disconnect();
    }, { rootMargin: "100% 0px" });
    const wide = window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)").matches;
    const seen = new IntersectionObserver(([e]) => {
      setInView(e.isIntersecting);
      if (e.isIntersecting && wide && !touched.current) setAuto(true);
    }, { threshold: 0.5 });
    ahead.observe(el);
    seen.observe(el);
    return () => {
      ahead.disconnect();
      seen.disconnect();
    };
  }, []);

  // The bar fills on the compositor; when it is full the next tab opens.
  useEffect(() => {
    const bar = bars.current[active];
    if (!auto || !bar) return;
    const a = bar.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], { duration: DWELL_MS, easing: "linear", fill: "forwards" });
    a.pause();
    a.onfinish = () => setActive((i) => (i + 1) % items.length);
    timer.current = a;
    return () => {
      a.cancel();
      timer.current = null;
    };
  }, [active, auto, items.length]);

  const holding = !inView || hover || focus;
  useEffect(() => {
    const a = timer.current;
    if (!a) return;
    if (holding) a.pause();
    else a.play();
  }, [holding, active, auto]);

  const pick = (i: number, moveFocus = false) => {
    touched.current = true;
    setAuto(false);
    setActive(i);
    if (moveFocus) tabs.current[i]?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    const last = items.length - 1;
    const to = e.key === "ArrowRight" ? (active === last ? 0 : active + 1) : e.key === "ArrowLeft" ? (active === 0 ? last : active - 1) : e.key === "Home" ? 0 : e.key === "End" ? last : null;
    if (to === null) return;
    e.preventDefault();
    pick(to, true);
  };

  return (
    <div
      ref={root}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false);
      }}
    >
      <div role="tablist" aria-label="Departments" onKeyDown={onKey} className="grid grid-cols-2 gap-2 rounded-2xl bg-muted/60 p-1.5 ring-1 ring-foreground/5 sm:grid-cols-4">
        {items.map((item, i) => {
          const Icon = ICONS[item.icon];
          const on = i === active;
          return (
            <button
              key={item.label}
              ref={(el) => {
                tabs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${id}-tab-${i}`}
              aria-selected={on}
              aria-controls={`${id}-panel-${i}`}
              tabIndex={on ? 0 : -1}
              onClick={() => pick(i)}
              className={cn(
                "relative flex items-center justify-center gap-2 overflow-hidden rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none sm:text-base",
                on ? "bg-background text-foreground shadow-sm ring-1 ring-foreground/10" : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
              )}
            >
              <Icon className={cn("size-4 shrink-0", on && "text-primary")} aria-hidden />
              <span className="truncate">{item.label}</span>
              <span
                ref={(el) => {
                  bars.current[i] = el;
                }}
                aria-hidden
                className={cn("absolute inset-x-3 bottom-0 h-0.5 origin-left scale-x-0 rounded-full bg-primary", !(auto && on) && "hidden")}
              />
            </button>
          );
        })}
      </div>

      <div className="mt-10 grid">
        {items.map((item, i) => {
          const on = i === active;
          return (
            <div
              key={item.label}
              role="tabpanel"
              id={`${id}-panel-${i}`}
              aria-labelledby={`${id}-tab-${i}`}
              tabIndex={on ? 0 : -1}
              className={cn(
                "grid items-center gap-8 transition-[opacity,visibility] duration-500 [grid-area:1/1] focus-visible:outline-none motion-reduce:transition-none lg:grid-cols-[2fr_3fr] lg:gap-12",
                on ? "visible opacity-100" : "invisible opacity-0",
              )}
            >
              <div className={cn("transition-[translate] duration-500 ease-out motion-reduce:transition-none", on ? "translate-y-0" : "translate-y-3")}>
                <h3 className="font-display text-3xl font-semibold tracking-tight">{item.title}</h3>
                <p className="mt-3 text-lg text-muted-foreground">{item.body}</p>
                <ul className="mt-5 space-y-2.5">
                  {item.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5">
                      <Check className="mt-1 size-4 shrink-0 text-primary" aria-hidden />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={cn("overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-foreground/10 transition-[scale] duration-700 ease-out motion-reduce:transition-none", on ? "scale-100" : "scale-[0.98]")}>
                {on || near ? (
                  <Image
                    src={item.image.src}
                    alt={item.image.alt}
                    width={item.image.width}
                    height={item.image.height}
                    sizes="(max-width: 1024px) 100vw, 660px"
                    className={cn("h-auto w-full", item.fade && "[mask-image:linear-gradient(to_bottom,#000_72%,transparent)]")}
                  />
                ) : (
                  <div style={{ aspectRatio: `${item.image.width} / ${item.image.height}` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
